import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, post, apiClient } from './client'
import type {
  KnowledgeSearchRequest,
  KnowledgeSearchResponse,
  GlossaryTerm,
  GlossarySearchParams,
  UpdateGlossaryStatusRequest,
  GlossaryRebuildRequest,
  SourceStatus,
  SystemStatus,
  DeduplicateGlossaryRequest,
  DeduplicateGlossaryResponse,
} from '@/types/knowledge'

export const knowledgeKeys = {
  all: ['knowledge'] as const,
  search: (request: KnowledgeSearchRequest) =>
    ['knowledge', 'search', request] as const,
  glossary: ['knowledge', 'glossary'] as const,
  glossaryList: (params: GlossarySearchParams) =>
    ['knowledge', 'glossary', 'list', params] as const,
  glossaryDetail: (termId: string) =>
    ['knowledge', 'glossary', 'detail', termId] as const,
  sources: () => ['knowledge', 'sources'] as const,
  status: () => ['knowledge', 'status'] as const,
}

async function searchKnowledge(request: KnowledgeSearchRequest): Promise<KnowledgeSearchResponse> {
  return post<KnowledgeSearchResponse>('/knowledge/search', request)
}

async function fetchGlossary(params: GlossarySearchParams): Promise<GlossaryTerm[]> {
  const queryParts: string[] = []
  if (params.query) queryParts.push(`query=${encodeURIComponent(params.query)}`)
  if (params.maxResults) queryParts.push(`maxResults=${params.maxResults}`)
  const qs = queryParts.length > 0 ? `?${queryParts.join('&')}` : ''
  return get<GlossaryTerm[]>(`/knowledge/glossary${qs}`)
}

async function fetchGlossaryTerm(termId: string): Promise<GlossaryTerm> {
  return get<GlossaryTerm>(`/knowledge/glossary/${encodeURIComponent(termId)}`)
}

async function fetchSources(): Promise<SourceStatus[]> {
  return get<SourceStatus[]>('/knowledge/sources')
}

async function fetchStatus(): Promise<SystemStatus> {
  return get<SystemStatus>('/knowledge/status')
}

async function updateGlossaryStatus(
  termId: string,
  request: UpdateGlossaryStatusRequest
): Promise<GlossaryTerm> {
  return apiClient<GlossaryTerm>(`/knowledge/glossary/${encodeURIComponent(termId)}/status`, {
    method: 'PATCH',
    body: request,
  })
}

async function rebuildGlossary(request: GlossaryRebuildRequest): Promise<void> {
  return post<void>('/knowledge/glossary/rebuild', request)
}

export function useKnowledgeSearch(request: KnowledgeSearchRequest) {
  return useQuery({
    queryKey: knowledgeKeys.search(request),
    queryFn: () => searchKnowledge(request),
    enabled: request.query.trim().length > 0,
    staleTime: 60_000,
  })
}

export function useGlossarySearch(params: GlossarySearchParams) {
  return useQuery({
    queryKey: knowledgeKeys.glossaryList(params),
    queryFn: () => fetchGlossary(params),
    staleTime: 60_000,
  })
}

export function useGlossaryTerm(termId: string | null) {
  return useQuery({
    queryKey: knowledgeKeys.glossaryDetail(termId ?? ''),
    queryFn: () => fetchGlossaryTerm(termId!),
    enabled: !!termId,
    staleTime: 60_000,
  })
}

export function useKnowledgeSources() {
  return useQuery({
    queryKey: knowledgeKeys.sources(),
    queryFn: fetchSources,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export function useKnowledgeStatus() {
  return useQuery({
    queryKey: knowledgeKeys.status(),
    queryFn: fetchStatus,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export function useUpdateGlossaryStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ termId, status }: { termId: string; status: UpdateGlossaryStatusRequest }) =>
      updateGlossaryStatus(termId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.glossary })
    },
  })
}

export function useRebuildGlossary() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: rebuildGlossary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.status() })
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.glossary })
    },
  })
}

async function deduplicateGlossary(
  request: DeduplicateGlossaryRequest
): Promise<DeduplicateGlossaryResponse> {
  return post<DeduplicateGlossaryResponse>('/knowledge/glossary/deduplicate', request)
}

export function useGlossaryDeduplicate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deduplicateGlossary,
    onSuccess: (_data, variables) => {
      if (!variables.dryRun) {
        queryClient.invalidateQueries({ queryKey: knowledgeKeys.glossary })
        queryClient.invalidateQueries({ queryKey: knowledgeKeys.status() })
      }
    },
  })
}
